import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { FiMessageSquare, FiFileText, FiUsers, FiZap, FiTarget, FiShield, FiSettings, FiMonitor, FiServer } from 'react-icons/fi';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import useGSAPScrollAnimations from '../hooks/useGSAPScrollAnimations';

// Register ScrollTrigger plugin
gsap.registerPlugin(ScrollTrigger);

const Features = () => {
  const sectionRef = useRef(null);
  const headerRef = useRef(null);
  const titleRef = useRef(null);
  const descriptionRef = useRef(null);
  const gridRef = useRef(null);
  const ctaRef = useRef(null);
  
  const { fadeInUp, staggerIn, scaleIn, parallax } = useGSAPScrollAnimations();

  useEffect(() => {
    // Header animations
    if (headerRef.current) {
      fadeInUp(headerRef.current, { delay: 0.1 });
    }
    
    if (titleRef.current) {
      // Advanced text animation for title
      gsap.fromTo(titleRef.current, 
        { 
          y: 60, 
          opacity: 0,
          scale: 0.8
        },
        { 
          y: 0, 
          opacity: 1,
          scale: 1,
          duration: 1.2,
          ease: "power3.out",
          scrollTrigger: {
            trigger: titleRef.current,
            start: "top 80%",
            end: "bottom 20%",
            toggleActions: "play none none reverse"
          }
        }
      );
    }
    
    if (descriptionRef.current) {
      fadeInUp(descriptionRef.current, { delay: 0.3 });
    }
    
    // Features grid animations
    if (gridRef.current) {
      const featureCards = gridRef.current.children;
      
      Array.from(featureCards).forEach((card, index) => {
        // Main card animation
        gsap.fromTo(card,
          {
            y: 80,
            opacity: 0,
            scale: 0.8,
            rotationY: 15
          },
          {
            y: 0,
            opacity: 1,
            scale: 1,
            rotationY: 0,
            duration: 0.8,
            ease: "power2.out",
            scrollTrigger: {
              trigger: card,
              start: "top 85%",
              end: "bottom 15%",
              toggleActions: "play none none reverse"
            },
            delay: index * 0.1
          }
        );
        
        // Icon floating animation
        const icon = card.querySelector('.feature-icon');
        if (icon) {
          gsap.to(icon, {
            y: -5,
            duration: 2,
            repeat: -1,
            yoyo: true,
            ease: "power2.inOut",
            delay: index * 0.2
          });
        }
        
        // Hover effects
        const cardElement = card.querySelector('.feature-card');
        if (cardElement) {
          cardElement.addEventListener('mouseenter', () => {
            gsap.to(cardElement, {
              y: -15,
              scale: 1.05,
              duration: 0.3,
              ease: "power2.out"
            });
            
            gsap.to(card.querySelector('.glow-effect'), {
              opacity: 0.3,
              scale: 1.2,
              duration: 0.3
            });
          });
          
          cardElement.addEventListener('mouseleave', () => {
            gsap.to(cardElement, {
              y: 0,
              scale: 1,
              duration: 0.3,
              ease: "power2.out"
            });
            
            gsap.to(card.querySelector('.glow-effect'), {
              opacity: 0,
              scale: 1,
              duration: 0.3
            });
          });
        }
      });
    }
    
    if (ctaRef.current) {
      scaleIn(ctaRef.current, { delay: 0.5 });
    }

    // Parallax background elements
    const backgroundElements = sectionRef.current?.querySelectorAll('.parallax-bg');
    if (backgroundElements) {
      backgroundElements.forEach((element, index) => {
        parallax(element, { 
          yPercent: index % 2 === 0 ? -20 : 20,
          scrollTrigger: {
            trigger: sectionRef.current,
            start: "top bottom",
            end: "bottom top",
            scrub: 1
          }
        });
      });
    }

    // Create floating particles
    const createFloatingParticles = () => {
      for (let i = 0; i < 15; i++) {
        const particle = document.createElement('div');
        particle.className = 'floating-particle absolute w-1 h-1 bg-white/20 rounded-full';
        particle.style.left = `${Math.random() * 100}%`;
        particle.style.top = `${Math.random() * 100}%`;
        sectionRef.current?.appendChild(particle);
        
        gsap.to(particle, {
          y: -100,
          opacity: 0,
          duration: 3 + Math.random() * 2,
          repeat: -1,
          delay: Math.random() * 2,
          ease: "power1.out"
        });
        
        gsap.to(particle, {
          x: (Math.random() - 0.5) * 50,
          duration: 4 + Math.random() * 2,
          repeat: -1,
          yoyo: true,
          ease: "power1.inOut"
        });
      }
    };
    
    createFloatingParticles();
  }, [fadeInUp, staggerIn, scaleIn, parallax]);
  const features = [
    {
      icon: FiMessageSquare,
      title: "Multi-AI Chat",
      description: "Chat with multiple AI models simultaneously and compare their responses in real-time.",
      gradient: "from-secondary-500 to-secondary-600",
      delay: 0,
    },
    {
      icon: FiFileText,
      title: "Smart Summaries",
      description: "Get intelligent summaries and insights from your conversations across all AI models.",
      gradient: "from-accent-500 to-accent-600",
      delay: 0.2,
    },
    {
      icon: FiUsers,
      title: "Custom AI Personalities",
      description: "Create and customize AI personalities tailored to your specific needs and preferences.",
      gradient: "from-primary-500 to-primary-600",
      delay: 0.4,
    },
    {
      icon: FiSettings,
      title: "MCP Integration",
      description: "Connect with external tools and services through Model Context Protocol for enhanced AI capabilities.",
      gradient: "from-cyan-500 to-blue-500",
      delay: 0.6,
    },
    {
      icon: FiMonitor,
      title: "Cross Platform",
      description: "Seamlessly access Bervin across all your devices - Windows, macOS, Linux, iOS, and Android.",
      gradient: "from-purple-500 to-pink-500",
      delay: 0.8,
    },
    {
      icon: FiServer,
      title: "Wide Provider Support",
      description: "Connect to 20+ AI providers including OpenAI, Claude, Gemini, Ollama, and many more in one platform.",
      gradient: "from-teal-500 to-green-500",
      delay: 1.0,
    },
    {
      icon: FiZap,
      title: "Lightning Fast",
      description: "Experience blazing-fast responses with our optimized AI infrastructure.",
      gradient: "from-yellow-500 to-orange-500",
      delay: 1.2,
    },
    {
      icon: FiTarget,
      title: "Precision Answers",
      description: "Get accurate, contextual responses powered by advanced AI reasoning.",
      gradient: "from-green-500 to-emerald-500",
      delay: 1.4,
    },
    {
      icon: FiShield,
      title: "Privacy First",
      description: "Your conversations are encrypted and secure with enterprise-grade protection.",
      gradient: "from-blue-500 to-indigo-500",
      delay: 1.6,
    },
  ];

  return (
    <section 
      ref={sectionRef}
      id="features" 
      className="py-20 px-6 relative overflow-hidden"
    >
      <div className="container mx-auto">
        {/* Background Elements with Parallax */}
        <div className="absolute inset-0 opacity-30">
          <div className="parallax-bg absolute top-0 left-1/4 w-64 h-64 bg-gradient-to-r from-secondary-500/20 to-accent-500/20 rounded-full blur-3xl"></div>
          <div className="parallax-bg absolute bottom-0 right-1/4 w-80 h-80 bg-gradient-to-r from-accent-500/20 to-primary-500/20 rounded-full blur-3xl"></div>
        </div>

        {/* Header */}
        <div
          ref={headerRef}
          className="text-center mb-16 relative z-10"
        >
          <h2
            ref={titleRef}
            className="text-4xl md:text-6xl font-display font-bold mb-6"
          >
            <span className="gradient-text">Powerful Features</span>
          </h2>
          <p
            ref={descriptionRef}
            className="text-xl text-gray-300 max-w-3xl mx-auto"
          >
            Discover the advanced capabilities that make Bervin the ultimate AI conversation platform
          </p>
        </div>

        {/* Features Grid */}
        <div ref={gridRef} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 relative z-10">
          {features.map((feature, index) => (
            <div
              key={feature.title}
              className="group relative"
            >
              {/* Card */}
              <div className="feature-card relative h-full p-8 glass border border-white/10 rounded-2xl overflow-hidden">
                {/* Glow Effect */}
                <div className={`glow-effect absolute inset-0 bg-gradient-to-br ${feature.gradient} opacity-0 rounded-2xl blur-xl`}></div>
                
                {/* Background Gradient */}
                <div className={`absolute inset-0 bg-gradient-to-br ${feature.gradient} opacity-0 group-hover:opacity-10 transition-opacity duration-300`}></div>
                
                {/* Icon Container */}
                <div
                  className={`feature-icon relative w-16 h-16 bg-gradient-to-r ${feature.gradient} rounded-xl flex items-center justify-center mb-6 shadow-lg`}
                >
                  <feature.icon className="w-8 h-8 text-white" />
                  
                  {/* Icon Glow Effect */}
                  <div className={`absolute inset-0 bg-gradient-to-r ${feature.gradient} rounded-xl blur-xl opacity-50 group-hover:opacity-75 transition-opacity duration-300`}></div>
                </div>

                {/* Content */}
                <div className="relative z-10">
                  <h3 className="text-2xl font-bold text-white mb-4 group-hover:text-white transition-colors duration-300">
                    {feature.title}
                  </h3>
                  <p className="text-gray-300 leading-relaxed">
                    {feature.description}
                  </p>
                </div>

                {/* Animated Border */}
                <div className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className={`absolute inset-0 rounded-2xl bg-gradient-to-r ${feature.gradient} opacity-20 blur-sm`}></div>
                </div>

                {/* Corner Accent */}
                <div className="absolute top-0 right-0 w-20 h-20 overflow-hidden rounded-2xl">
                  <div className={`absolute -top-10 -right-10 w-20 h-20 bg-gradient-to-br ${feature.gradient} opacity-20 rotate-45`}></div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div
          ref={ctaRef}
          className="text-center mt-16 relative z-10"
        >
          <motion.button
            whileHover={{ 
              scale: 1.05,
              boxShadow: "0 20px 40px rgba(168, 85, 247, 0.3)"
            }}
            whileTap={{ scale: 0.95 }}
            onClick={(e) => {
              e.preventDefault();
              try {
                window.open('https://bervin.appiworks.com/app', '_blank', 'noopener,noreferrer');
              } catch (error) {
                window.location.href = 'https://bervin.appiworks.com/app';
              }
            }}
            className="px-8 py-4 bg-gradient-to-r from-secondary-500 to-accent-500 rounded-xl font-semibold text-white text-lg shadow-xl hover-lift cursor-pointer"
          >
            Witness the Magic
          </motion.button>
        </div>
      </div>
    </section>
  );
};

export default Features;
